import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { usePublicMode } from "../hooks/usePublicMode";
import { NotifyMeButton } from "../components/NotifyMeButton";
import { toast } from "sonner";
import { Droplet, Sun, Sprout, ArrowLeft } from "lucide-react";

const CARE_BG = "https://images.unsplash.com/photo-1701186598427-5eb8f1717d2a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxtb29keSUyMGdyZWVuaG91c2UlMjBpbnRlcmlvcnxlbnwwfHx8fDE3ODM5MTUwOTV8MA&ixlib=rb-4.1.0&q=85";

export default function PlantDetailPage() {
  const { slug } = useParams();
  const [plant, setPlant] = useState(null);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();
  const { publicMode } = usePublicMode();
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    api.get(`/plants/${slug}`).then(({ data }) => setPlant(data)).catch(() => setPlant(null)).finally(() => setLoading(false));
    window.scrollTo(0, 0);
  }, [slug]);

  if (loading) {
    return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 font-display text-3xl text-[var(--gu-muted)]">Gathering leaves…</div>;
  }
  if (!plant) {
    return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 font-display text-3xl">Plant not found.</div>;
  }

  const addToCart = () => {
    add(plant, qty);
    toast.success(`${plant.name} added to your basket.`);
  };

  return (
    <div data-testid="plant-detail-page">
      {/* Top light section */}
      <section className="bg-[var(--gu-bg)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-8">
          <button onClick={() => navigate(-1)} className="overline flex items-center gap-2 text-[var(--gu-muted)] hover:text-[var(--gu-primary)] transition-colors mb-8">
            <ArrowLeft size={14} /> Back
          </button>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-12 gap-12 pb-24">
          {/* Sticky details left */}
          <div className="col-span-12 lg:col-span-5 lg:sticky lg:top-32 lg:self-start">
            <div className="overline text-[var(--gu-muted)] mb-4">{plant.seller_name}</div>
            <h1 data-testid="plant-name" className="font-display text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95]">{plant.name}</h1>
            <div className="italic text-[var(--gu-muted)] mt-3">{plant.scientific_name}</div>

            <div className="mt-10">
              <div data-testid="plant-price" className="text-3xl font-display">${plant.price.toFixed(2)}</div>
              <div className="overline text-[var(--gu-muted)] mt-2">{plant.stock > 0 ? `${plant.stock} in the studio` : "Sold out"}</div>
            </div>

            <p className="mt-8 text-[var(--gu-muted)] leading-relaxed max-w-lg">{plant.description}</p>

            {/* qty + add to cart (or Notify me when public) */}
            <div className="mt-10">
              {publicMode ? (
                <NotifyMeButton plant={plant} size="large" />
              ) : (
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-[var(--gu-border)]">
                    <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-11 h-12 hover:bg-[var(--gu-surface-2)]" data-testid="qty-dec">−</button>
                    <div className="w-10 text-center text-sm" data-testid="qty-value">{qty}</div>
                    <button onClick={() => setQty(qty + 1)} className="w-11 h-12 hover:bg-[var(--gu-surface-2)]" data-testid="qty-inc">+</button>
                  </div>
                  <button onClick={addToCart} disabled={plant.stock === 0} data-testid="add-to-cart-btn" className="flex-1 bg-[var(--gu-primary)] text-[var(--gu-bg)] py-4 overline hover:bg-[var(--gu-primary)]/90 transition-colors disabled:opacity-40">
                    Add to basket
                  </button>
                </div>
              )}
            </div>

            {/* quick facts */}
            <div className="mt-12 grid grid-cols-3 gap-4 border-y border-[var(--gu-border)] py-6">
              <QuickFact icon={<Sun size={16} strokeWidth={1.5} />} label="Light" value={plant.care_light} />
              <QuickFact icon={<Droplet size={16} strokeWidth={1.5} />} label="Water" value={plant.care_water} />
              <QuickFact icon={<Sprout size={16} strokeWidth={1.5} />} label="Care" value={plant.care_level} />
            </div>
          </div>

          {/* Scrolling gallery right */}
          <div className="col-span-12 lg:col-span-7 space-y-6">
            {(plant.images || []).map((src) => (
              <div key={src} className="aspect-[4/5] w-full overflow-hidden bg-[var(--gu-surface-2)]">
                <img src={src} alt={plant.name} className="w-full h-full object-cover" />
              </div>
            ))}
            {/* Second image variation */}
            <div className="aspect-[4/5] w-full overflow-hidden bg-[var(--gu-surface-2)]">
              <img src={plant.images?.[0]} alt={plant.name} className="w-full h-full object-cover" style={{ filter: 'saturate(0.9) brightness(0.95)' }} />
            </div>
          </div>
        </div>
      </section>

      {/* DARK MOODY CARE SECTION */}
      <section className="relative bg-[var(--gu-dark-bg)] text-[var(--gu-dark-text)] overflow-hidden noise-overlay">
        <div className="absolute inset-0 opacity-30">
          <img src={CARE_BG} className="w-full h-full object-cover" alt="" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--gu-dark-bg)] via-[var(--gu-dark-bg)]/70 to-[var(--gu-dark-bg)]" />
        </div>
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-12 py-32">
          <div className="overline text-[var(--gu-dark-muted)] mb-6">Care notes</div>
          <h2 className="font-display text-5xl lg:text-6xl font-light tracking-tighter leading-[0.95] max-w-3xl">
            How to keep <em className="not-italic text-[var(--gu-accent)]">{plant.name}</em> alive and glad.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mt-20">
            <CareCard title="Light" body={`Give it ${plant.care_light} light. Rotate the pot a quarter turn each week so it grows evenly toward the window.`} />
            <CareCard title="Water" body={`Water on a ${plant.care_water} schedule. Let the top inch of soil dry between drinks — check with a finger, not a calendar.`} />
            <CareCard title="Difficulty" body={`Care level: ${plant.care_level}. ${plant.care_level === "easy" ? "Forgiving of forgetting." : plant.care_level === "moderate" ? "Wants a rhythm." : "A slow relationship. Worth it."}`} />
          </div>
        </div>
      </section>
    </div>
  );
}

function QuickFact({ icon, label, value }) {
  return (
    <div>
      <div className="text-[var(--gu-muted)]">{icon}</div>
      <div className="overline text-[var(--gu-muted)] mt-2">{label}</div>
      <div className="text-sm mt-1 capitalize">{value}</div>
    </div>
  );
}

function CareCard({ title, body }) {
  return (
    <div className="border-t border-[var(--gu-dark-border)] pt-6">
      <div className="font-display text-3xl mb-4">{title}</div>
      <p className="text-[var(--gu-dark-muted)] leading-relaxed">{body}</p>
    </div>
  );
}
